import PropTypes from 'prop-types'
import { useState } from 'react'

import {
  PanelHeader,
  PanelTitle,
  PanelDetails,
  PanelSection
} from 'components/attacks/details/AttackStyles'
import ApiTrigger from 'components/common/ApiTrigger'
import Button from 'components/common/Button'
import InputTextField from 'components/common/InputTextField'
import Text, { styles, colors } from 'components/common/Text'
import TextArea from 'components/common/TextArea'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { unescapeHTMLEntities } from 'utils/strings'

function SubtitleText(props) {
  return <Text as="h5" css={{ marginBottom: theme.space[1] }} {...props} />
}

export default function InfraAttackResultsSection({ attack, doFetch }) {
  const { currentTeamId } = useAuth()
  const attackAnnotations = attack?.annotations ?? {}
  const [annotations, setAnnotations] = useState(attackAnnotations)
  const [isEditing, setIsEditing] = useState(false)
  const { haltReason = '', haltReference = '' } = attackAnnotations

  return (
    <PanelSection>
      <PanelHeader>
        <PanelTitle>Results</PanelTitle>
        {!isEditing && (
          <Button
            secondary
            css={{ marginLeft: 'auto' }}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </PanelHeader>
      <PanelDetails
        css={{
          display: 'grid',
          gap: theme.space[4]
        }}
      >
        {haltReason && (
          <div>
            <SubtitleText css={{ marginBottom: theme.space[2] }}>
              Halt Reason
            </SubtitleText>
            <Text as="p">{unescapeHTMLEntities(haltReason)}</Text>
          </div>
        )}
        {haltReference && (
          <div>
            <SubtitleText css={{ marginBottom: theme.space[2] }}>
              Halt Metrics Information
            </SubtitleText>
            <TrackedExternalLink
              overflowEllipsis
              underline
              css={[styles.p, colors.normal]}
              href={haltReference}
            >
              {haltReference}
            </TrackedExternalLink>
          </div>
        )}

        <div>
          <SubtitleText css={{ marginBottom: theme.space[2] }}>
            Notes and Observations
          </SubtitleText>
          {isEditing ? (
            <TextArea
              css={{ height: 'auto' }}
              placeholder="Add your notes and observations"
              value={annotations.notes}
              onChange={({ target: { value } }) =>
                setAnnotations({ ...annotations, notes: value })
              }
            />
          ) : (
            <div>
              <Text as="p">
                {annotations.notes || 'No notes or observations.'}
              </Text>
            </div>
          )}
        </div>

        <div>
          <SubtitleText>Metrics Reference</SubtitleText>
          {isEditing ? (
            <InputTextField
              name="metricsLink"
              placeholder="Link to external metrics"
              type="url"
              value={annotations.metrics}
              onChange={({ target: { value } }) =>
                setAnnotations({ ...annotations, metrics: value })
              }
            />
          ) : annotations.metrics ? (
            <TrackedExternalLink overflowEllipsis href={annotations.metrics}>
              <Text underline as="p" color="normal" css={{ display: 'inline' }}>
                {annotations.metrics}
              </Text>
            </TrackedExternalLink>
          ) : (
            <Text as="p">{annotations.metrics || 'No metrics reference.'}</Text>
          )}
        </div>

        {isEditing && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            {attack?.annotations && (
              <Button
                secondary
                css={{ marginRight: theme.space[3] }}
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            )}
            <ApiTrigger
              primary
              as={Button}
              errorToastMessage="There was an issue saving results. Please try again later."
              request={{
                url: `/attacks/${attack.guid}/annotations?teamId=${currentTeamId}`,
                method: 'PUT',
                data: { ...attack?.annotations, ...annotations }
              }}
              successToastMessage="Attack results saved."
              onSuccess={() => {
                doFetch()
                setIsEditing(false)
              }}
            >
              Save
            </ApiTrigger>
          </div>
        )}
      </PanelDetails>
    </PanelSection>
  )
}

InfraAttackResultsSection.propTypes = {
  attack: PropTypes.object,
  doFetch: PropTypes.func
}
